import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Button,
  Indicators,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Textarea,
} from 'ui'
import CreatableSelect from 'react-select/creatable'
import { useDebounce } from 'utils'
import { useNavigate } from 'react-router-dom'
import { useUpdateProfileMutation } from '../../../queries/useUpdateProfileMutation'
import { useKeywordSearch } from '../../../queries/useKeywordSearch'
import { useSuggestKeywordsMutation } from '../../../queries/useSuggestKeywordsMutation'
import { useCreateKeywordMutation } from '../../../queries/useCreateKeywordMutation'
import { useCurrentUser } from '../../../queries/useCurrentUser'
import { useAdminProfileById } from '../../../queries/useAdminProfileById'
import clsx from 'clsx'
import { useSkipOnboarding } from '../../../hooks'
import { useQueryClient } from '@tanstack/react-query'

const schema = yup.object({
  contentDescription: yup
    .string()
    .required('Cannot be blank')
    .min(75, 'Must be at least 75 characters'),
  collaboratorDescription: yup
    .string()
    .required('Cannot be blank')
    .min(75, 'Must be at least 75 characters'),
  keywords: yup.array().of(yup.string().required()).optional(),
})
export type EducatorProfilePart2 = yup.InferType<typeof schema>

interface EducatorStepTwoProps {
  onSuccess?: () => void
  hideNavigation?: boolean
  editMode?: boolean
  profileId?: string // Added profileId parameter
  targetField?: string
}

export const EducatorStepTwoForm: React.FC<EducatorStepTwoProps> = ({
  onSuccess,
  hideNavigation = false,
  editMode = false,
  profileId,
  targetField,
}) => {
  // Use admin profile query if profileId is provided, otherwise use current user
  const { data: user, isLoading: isCurrentUserLoading } = useCurrentUser()
  const {
    data: adminProfile,
    isLoading: isAdminProfileLoading,
  } = useAdminProfileById(profileId)

  // Determine which profile data to use
  const profileData = profileId ? adminProfile : user
  const isLoading = profileId ? isAdminProfileLoading : isCurrentUserLoading
  const userId = profileId || (user?.userId as string)

  const navigate = useNavigate()

  const form = useForm<EducatorProfilePart2>({
    defaultValues: {
      contentDescription: '',
      collaboratorDescription: '',
      keywords: [],
    } as EducatorProfilePart2,
    reValidateMode: 'onChange',
    resolver: yupResolver(schema as any) as any,
  })
  const contentDescription = form.watch('contentDescription')
  const collaboratorDescription = form.watch('collaboratorDescription')

  const needsContentDescription = (contentDescription?.length ?? 0) < 75
  const needsCollaboratorDescription =
    (collaboratorDescription?.length ?? 0) < 75
  const hasDescriptions =
    !needsContentDescription && !needsCollaboratorDescription

  const description = hasDescriptions
    ? `${contentDescription} ${collaboratorDescription}`
    : ''

  let prompt = `Please enter ${
    75 - (collaboratorDescription?.length ?? 0)
  } or more characters about who would best help your students with their classes.`
  if (needsContentDescription) {
    prompt = `Please enter ${
      75 - (contentDescription?.length ?? 0)
    } or more characters about what types of classes do you teach.`
  }

  useEffect(() => {
    if (profileData) {
      form.setValue(
        'contentDescription',
        profileData?.contentDescription as string
      )
      form.setValue(
        'collaboratorDescription',
        profileData?.collaboratorDescription as string
      )
      form.setValue('keywords', profileData?.keywords as string[])
    }
  }, [profileData, form])

  const [suggestedKeywords, setSuggestedKeywords] = useState<
    { id: number; keyword: string }[]
  >([])
  const {
    mutateAsync: suggestKeywords,
    isPending: loadingKeywords,
  } = useSuggestKeywordsMutation()

  // Track the current input value for search
  const [keywordInput, setKeywordInput] = useState('')
  const debouncedKeywordInput = useDebounce(keywordInput, 300)

  // Search for keywords based on debounced input
  const { data: searchedKeywords } = useKeywordSearch(
    debouncedKeywordInput,
    debouncedKeywordInput.length >= 2
  )

  const handleGenerateKeywords = async () => {
    if (description && description.length >= 75) {
      try {
        const keywords = await suggestKeywords({ userId, description })
        setSuggestedKeywords(keywords)
        form.setValue(
          'keywords',
          keywords?.map((k) => k.keyword).filter((_, i) => i < 4) as string[]
        )
      } catch (error) {
        console.error('Failed to generate keywords:', error)
      }
    }
  }
  const { mutateAsync: createKeyword } = useCreateKeywordMutation()
  const [userAddedKeywords, setUserAddedKeywords] = useState([] as string[])
  const addKeyword = async (keyword: string) => {
    setUserAddedKeywords([...(userAddedKeywords ?? []), keyword])
    await createKeyword({ keyword })
  }
  const userKeywords = profileData?.keywords ?? []
  const currentKeywords = form.watch('keywords') ?? []
  const usableKeywords = Array.from(
    new Set([
      ...(suggestedKeywords?.map((k) => k.keyword) ?? []),
      ...(searchedKeywords?.map((k) => k.keyword) ?? []),
      ...userKeywords,
      ...userAddedKeywords,
      ...currentKeywords,
    ])
  )

  const { mutateAsync: updateProfile } = useUpdateProfileMutation()
  const queryClient = useQueryClient()

  const handleSubmitForm = (redirect?: string) => async (
    values: EducatorProfilePart2
  ) => {
    await updateProfile({
      userId: userId,
      contentDescription: values.contentDescription as string,
      collaboratorDescription: values.collaboratorDescription as string,
      keywords: values.keywords as string[],
    })
    queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    // Call onSuccess callback if provided
    if (onSuccess) {
      onSuccess()
    }

    // Only navigate if redirect is provided and we're not in edit mode
    if (redirect && !editMode) {
      navigate(redirect)
    }
  }

  const skipOnboarding = useSkipOnboarding()

  const onSaveDraft = async () => {
    // Try to save current form data as best-effort, but always skip regardless of validation
    const values = form.getValues()
    try {
      await handleSubmitForm(undefined)(values)
    } catch {
      // Ignore errors - we're skipping anyway
    }
    skipOnboarding()
  }

  // const userKeywords = user?.keywords ?? []
  // const usableKeywords = Array.from(
  //   new Set([...userKeywords, ...(keywords?.map((k) => k.keyword) ?? [])])
  // ).sort((a, b) => {
  //   const aIndex = userKeywords.indexOf(a)
  //   const bIndex = userKeywords.indexOf(b)

  //   // If both keywords are in userKeywords, sort by their original order
  //   if (aIndex !== -1 && bIndex !== -1) {
  //     return aIndex - bIndex
  //   }

  //   // If only a is in userKeywords, it should come first
  //   if (aIndex !== -1) return -1

  //   // If only b is in userKeywords, it should come first
  //   if (bIndex !== -1) return 1
  //   return 0
  // })

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(
          handleSubmitForm(editMode ? undefined : '/onboard/educator/three')
        )}
        className={clsx(isLoading && 'opacity-50')}
      >
        <div className="flex flex-col">
          <FormLabel>What types of classes do you teach?</FormLabel>
          <p
            className={`font-body text-sm mt-3 ${
              targetField === 'contentDescription'
                ? 'bg-yellow-100 animate-pulse p-3 rounded-md border-l-4 border-yellow-400'
                : ''
            }`}
          >
            Tell us about the content, subjects and grade level(s) you teach and
            how many years you've been in education. Please provide a sentence
            or two about the strategies you use in your classroom (i.e. hands
            on, project based, classroom challenges, creative problem solving,
            etc.).
          </p>
          <p className="font-body text-sm text-body/70 my-3">
            Ex. I teach physical science to 7th grade students in the Mesa
            school district. I have taught for 8 years at the same school! I
            believe that students learn best when they connect to the content
            through hands-on and inquiry-based learning experiences. I
            facilitate 2-3 project-based learning experiences in my classroom
            during the school year.
          </p>
          <FormField
            control={form.control}
            name="contentDescription"
            render={({ field }) => (
              <FormItem className="w-full col-span-3 relative">
                <FormLabel className="hidden">Types of Classes</FormLabel>
                <FormControl>
                  <>
                    <Textarea {...field} />
                    {(field.value?.length || 0) < 75 && (
                      <div className="mt-1 text-xs text-body/70">
                        {75 - (field.value?.length || 0)} more characters
                        required
                      </div>
                    )}
                  </>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-col mt-8">
          <FormLabel>
            Describe the Types of Professionals Who Would Best Help Your
            Classes.
          </FormLabel>
          <p
            className={`font-body text-sm mt-3 ${
              targetField === 'collaboratorDescription'
                ? 'bg-yellow-100 animate-pulse p-3 rounded-md border-l-4 border-yellow-400'
                : ''
            }`}
          >
            What are the key skills and attributes you are looking for in
            professionals to engage with your classroom or school?
          </p>
          <p className="font-body text-sm text-body/70 my-3">
            Ex. A professional should be able to discuss science content areas
            and how they connect to my content and the importance of critical
            thinking in their career field. I would like someone, support with
            classroom projects (i.e. initiating, answering students' questions,
            moving projects forward, etc.), I would also like them to be able to
            discuss career pathways in their field (for example in a career
            panel with other professionals).
          </p>
          <FormField
            control={form.control}
            name="collaboratorDescription"
            render={({ field }) => (
              <FormItem className="w-full col-span-3 relative">
                <FormLabel className="hidden">Types of Professionals</FormLabel>
                <FormControl>
                  <>
                    <Textarea {...field} />
                    {(field.value?.length || 0) < 75 && (
                      <div className="mt-1 text-xs text-body/70">
                        {75 - (field.value?.length || 0)} more characters
                        required
                      </div>
                    )}
                  </>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* <div className="flex flex-col mt-8">
          <FormLabel>Suggested Keywords</FormLabel>
          <p className="font-body text-sm mt-1">
            Real-world keywords help us link your profile to the things that
            professionals do every day. Based on what you've told us so far,
            adding the following keywords to your profile will expose you to
            professionals with similar interests. You can always uncheck
            keywords that don't apply and add others.
          </p>
          <p className="font-body text-sm mt-3 text-body/70">
            Note: For matching to work effectively, please choose 4-6 keywords.
          </p>
          <FormField
            control={form.control}
            name="keywords"
            render={({ field: { value, onChange } }) => (
              <div className="grid grid-cols-8 gap-2 mt-4 relative">
                {usableKeywords?.map((keyword) => (
                  <FormItem key={keyword} className="col-span-3">
                    <FormControl>
                      <Checkbox
                        checked={value?.includes(keyword)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            onChange([...(value ?? []), keyword])
                          } else {
                            onChange(value?.filter((l) => l !== keyword))
                          }
                        }}
                      />
                    </FormControl>
                    <FormLabel className="text-sm ml-2 text-body">
                      {keyword}
                    </FormLabel>
                  </FormItem>
                ))}
                {hasDescriptions ? null : (
                  <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex align-center justify-center text-center font-body">
                    <p className="m-auto p-3 border border-contentareabackgroundalt bg-white/80 rounded text-sm">
                      Please enter the descriptions above to reveal your
                      keywords.
                    </p>
                  </div>
                )}
                {loadingKeywords ? <Indicators.AbsoluteOverlay /> : null}
              </div>
            )}
          />
        </div> */}

        <div className="flex flex-col mt-4">
          <FormLabel>Suggested Keywords</FormLabel>
          <p
            className={`font-body text-sm mt-1 ${
              targetField === 'keywords'
                ? 'bg-yellow-100 animate-pulse p-3 rounded-md border-l-4 border-yellow-400'
                : ''
            }`}
          >
            EPC will add keywords to your profile based on what you've already
            indicated. Real-world keywords help us link your profile to things
            that teachers do every day and will expose you to professionals with
            similar interests and skills.{' '}
            <span className="font-bold">
              You can always uncheck keywords that don't apply and add others.
            </span>
          </p>
          <p className="font-body text-sm text-body/70 mt-1">
            Keyword examples: Project-Based Learning, collaboration, inquiry,
            team-work, critical thinking, math, science, technology, technical
            writing, etc.
          </p>
          <p className="font-body text-sm mt-3 text-body/70">
            Note: For matching to work effectively,{' '}
            <span className="font-bold">please choose 4-6 keywords.</span>
          </p>

          <div className="mt-4 mb-2">
            <Button
              type="button"
              variant="default"
              onClick={handleGenerateKeywords}
              disabled={!hasDescriptions || loadingKeywords}
              className="text-sm"
            >
              {loadingKeywords ? (
                <>
                  <Indicators.Spinner size="sm" className="mr-2" />
                  Generating Keywords...
                </>
              ) : (
                <>
                  {suggestedKeywords.length > 0 || userKeywords.length > 0
                    ? 'Regenerate Keywords'
                    : 'Generate Keywords'}
                </>
              )}
            </Button>
            {!hasDescriptions && (
              <p className="text-xs text-body/70 mt-1">
                Complete both descriptions above to generate keywords.
              </p>
            )}
          </div>

          <div className="relative py-3">
            <FormField
              control={form.control}
              name="keywords"
              render={({ field }) => (
                <FormItem className="relative">
                  <FormLabel className="hidden">Keywords</FormLabel>
                  <FormControl>
                    <CreatableSelect
                      isDisabled={form.formState.isSubmitting}
                      isMulti
                      isClearable={false}
                      placeholder="Select or create keywords"
                      options={usableKeywords.map((keyword) => ({
                        value: keyword,
                        label: keyword,
                      }))}
                      onInputChange={(inputValue) => {
                        setKeywordInput(inputValue)
                      }}
                      value={
                        usableKeywords
                          ?.filter((keyword) => field.value?.includes(keyword))
                          .map((keyword) => ({
                            value: keyword,
                            label: keyword,
                          })) || []
                      }
                      onChange={(selectedOptions) => {
                        field.onChange(
                          selectedOptions.map((option) => option.value)
                        )
                      }}
                      onCreateOption={(inputValue) => {
                        console.log('onCreateOption', inputValue)
                        field.onChange([...(field.value || []), inputValue])
                        addKeyword(inputValue)
                      }}
                      classNames={{
                        control: () =>
                          'flex w-full rounded-md border border-input bg-background py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <p className="text-xs font-body text-body/70 md:mr-28 mt-1">
              When you start typing, we'll suggest keywords that are already in
              our system. Selecting existing keywords will help with accuracy,
              and, you won't have to type as much nor worry about spelling
              errors!
            </p>
            {usableKeywords.length === 0 && !loadingKeywords ? (
              <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex align-center justify-center text-center font-body">
                <p className="m-auto p-3 border border-contentareabackgroundalt bg-white/80 rounded text-sm">
                  {hasDescriptions
                    ? "Click 'Generate Keywords' above to get suggested keywords based on your descriptions."
                    : `Please complete both descriptions above, then click 'Generate Keywords'.`}
                </p>
              </div>
            ) : null}
            {loadingKeywords ? <Indicators.AbsoluteOverlay /> : null}
          </div>
        </div>

        {!hideNavigation ? (
          <div className="flex-col flex mt-12 w-full">
            <Button
              type="submit"
              disabled={
                form.formState.isSubmitting || !form.watch('keywords')?.length
              }
              variant="default"
              className="mx-auto uppercase"
            >
              {form.formState.isSubmitting ? (
                <Indicators.Spinner
                  size="lg"
                  className="mt-auto mr-2 mb-auto ml-auto"
                />
              ) : null}
              <span>Next: Review Your Bio &gt;</span>
            </Button>
            <Button
              type="button"
              variant="link"
              className="text-actionable underline text-sm"
              onClick={onSaveDraft}
            >
              <span>Save Draft and Finish Later</span>
            </Button>
          </div>
        ) : (
          <div className="flex-col flex mt-8 w-full">
            <Button
              type="submit"
              disabled={
                form.formState.isSubmitting || !form.watch('keywords')?.length
              }
              variant="default"
              className="ml-auto"
            >
              {form.formState.isSubmitting ? (
                <Indicators.Spinner
                  size="lg"
                  className="mt-auto mr-2 mb-auto ml-auto"
                />
              ) : null}
              <span>{editMode ? 'Update Profile' : 'Save Changes'}</span>
            </Button>
          </div>
        )}
      </form>
    </Form>
  )
}
